import { App, Button, Popover, Select, Spin, Tag, Tooltip } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useKeyboardShortcutsContextOptional } from '../../../contexts/KeyboardShortcutsContext';
import useFormatTimestamp from '../../../hooks/useFormatTimestamp';
import useOpenFile from '../../../hooks/useOpenFile';
import { useTags } from '../../../hooks/useTags';
import { useColorScheme } from '../../../hooks/useColorScheme/useColorScheme';
import type File from '../../../interfaces/File';
import { FileService } from '../../../services/fileApiService';
import { getTagPresentationStyle } from '../../../utils/colorsUtilis';
import {
  dateFromTicks,
  getFileTitle,
  getFolderHex,
  removeShortcutExtension,
} from '../../../utils/utils';
import { Icon } from '../../Core/Icon';
import FileIcon from '../FileIcon';
import TruncatedTooltip from '../TruncatedTooltip';

const fileService = new FileService();

interface FilterState {
  keyword: string;
  includeFolder: boolean;
  includeTags: boolean;
  offset: number;
  count: number;
  orderColumn: number;
  exactMatch: boolean;
}

interface GlobalSearchFieldProps {
  /** Optional class on the Select root (e.g. mobile popover width). */
  className?: string;
  /** Called after a file is selected and navigation starts (e.g. close mobile popover). */
  onAfterSelect?: () => void;
}

export default function GlobalSearchField({ className, onAfterSelect }: GlobalSearchFieldProps) {
  // Ant Design notification instance for showing feedback messages
  const { notification } = App.useApp();

  // i18next translation function
  const { t } = useTranslation();

  // Custom hook to retrieve available tags
  const { tags } = useTags();
  const { resolvedColorScheme } = useColorScheme();
  const isDarkMode = resolvedColorScheme === 'dark';

  // Custom hook that provides a file navigation handler
  const { redirectFileToLocation } = useOpenFile();

  // Currently selected file's UID, if any
  const [selectedFileUid, setSelectedFileUid] = useState<number | null>(null);

  // Key to force Select re-render when a file is selected (to reset internal state)
  const [selectKey, setSelectKey] = useState<number>(0);

  // Dropdown open/close state for the select
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Timestamp formatting hook
  const formatTimestamp = useFormatTimestamp();

  // Loading state for file search requests
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // List of files from the search API
  const [files, setFiles] = useState<File[]>([]);

  // Total number of files matching the filters (for pagination)
  const [filesTotal, setFilesTotal] = useState<number>(0);

  // Search/filter configuration state
  const [filters, setFilters] = useState<FilterState>({
    keyword: '',
    includeFolder: true,
    includeTags: true,
    offset: 0,
    count: 10,
    orderColumn: 0,
    exactMatch: false,
  });

  // Ref to the select element for focus control (Primary+K via keyboard shortcut engine)
  const selectRef = useRef<any>(null);
  const keyboardShortcutsContext = useKeyboardShortcutsContextOptional();

  const focusSearchField = useCallback(() => {
    if (selectRef.current) {
      selectRef.current.focus();
      setIsOpen(true);
    }
  }, []);

  useEffect(() => {
    keyboardShortcutsContext?.registerFocusSearch(focusSearchField);
    return () => keyboardShortcutsContext?.registerFocusSearch(null);
  }, [focusSearchField, keyboardShortcutsContext]);

  // Ref for debounce timeout ID
  const debounceTimeoutRef = useRef<number | null>(null);

  // Ref for whether the dropdown should remain open
  const shouldKeepOpenRef = useRef<boolean>(false);

  /** Bumps on each search so stale responses cannot overwrite newer results. */
  const searchGenerationRef = useRef(0);
  const searchAbortRef = useRef<AbortController | null>(null);

  /**
   * Handle changes to the search input.
   * When the input changes, update the filters and reset pagination.
   * Note: Debouncing and API calls are handled in useEffect.
   */
  const handleSearch = useCallback((newValue: string) => {
    shouldKeepOpenRef.current = true;
    setFilters((prevFilters) => ({
      ...prevFilters,
      keyword: newValue,
      offset: 0,
    }));
  }, []);

  /**
   * Fetch files from the API according to current filters.
   * If 'reset' is true, overwrite the file list, otherwise append to it.
   */
  const getFiles = useCallback(
    async (newFilters: FilterState, reset = false) => {
      searchAbortRef.current?.abort();
      const abortController = new AbortController();
      searchAbortRef.current = abortController;
      const generation = ++searchGenerationRef.current;

      setIsLoading(true);

      await fileService
        .searchFiles({ ...newFilters }, { signal: abortController.signal })
        .then((response) => {
          if (generation !== searchGenerationRef.current) {
            return;
          }
          // Use returned files or an empty array if undefined
          const result = response?.myFiles || [];

          if (reset) {
            setFiles(result);
          } else {
            setFiles((prevFiles) => [...prevFiles, ...result]);
          }

          setFilesTotal(response.total);
        })
        .catch((error) => {
          if (generation !== searchGenerationRef.current) {
            return;
          }
          if (
            error?.name === 'CanceledError' ||
            error?.code === 'ERR_CANCELED' ||
            abortController.signal.aborted
          ) {
            return;
          }
          // Show a generic error notification
          notification.error({
            title: t('errors.genericError'),
          });
        })
        .finally(() => {
          if (generation !== searchGenerationRef.current) {
            return;
          }
          setIsLoading(false);
          // Keep dropdown open if we're still searching
          if (newFilters.keyword.trim() && shouldKeepOpenRef.current) {
            setIsOpen(true);
          }
        });
    },
    [notification, t],
  );

  /**
   * Handle toggling the 'Exact Match' search option.
   * If a keyword is present, run the search again.
   * Otherwise, just toggle the state without making requests.
   */
  const handleExactMatchChange = useCallback(() => {
    const newExactMatch = !filters.exactMatch;
    const hasKeyword = filters.keyword.trim().length > 0;

    if (hasKeyword) {
      // If keyword is present, refetch with new exactMatch flag, reset pagination.
      const newFilters = {
        ...filters,
        exactMatch: newExactMatch,
        offset: 0,
      };
      setFilters(newFilters);
      getFiles(newFilters, true).then(() => {});
    } else {
      // If keyword is absent, update the state only.
      setFilters((prevFilters) => ({
        ...prevFilters,
        exactMatch: newExactMatch,
      }));
    }
  }, [filters, getFiles]);

  /**
   * Handles loading more files when scrolled to the bottom of the dropdown.
   * Increments the offset in the filters and triggers the next search page.
   */
  const handleLoadMore = useCallback(() => {
    // Only load more if we haven't reached the total
    if (files.length < filesTotal && !isLoading) {
      const newFilters = {
        ...filters,
        offset: filters.offset + filters.count,
      };
      setFilters(newFilters);
      getFiles(newFilters, false).then(() => {});
    }
  }, [filters, getFiles, files.length, filesTotal, isLoading]);

  /**
   * Debounced effect: runs file search whenever the search keyword changes.
   * Debounce delay is 300ms.
   * If keyword is empty, reset results and close dropdown.
   */
  useEffect(() => {
    // If the keyword is empty, clear results and close dropdown.
    if (!filters.keyword.trim()) {
      searchAbortRef.current?.abort();
      searchGenerationRef.current += 1;
      setFiles([]);
      setSelectedFileUid(null);
      setIsOpen(false);
      setIsLoading(false);
      shouldKeepOpenRef.current = false;
      return;
    }

    // When searching, forcibly keep the dropdown open
    shouldKeepOpenRef.current = true;
    setIsOpen(true);

    // Clear previous debounce timeout if present
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce the API search call by 300ms
    debounceTimeoutRef.current = window.setTimeout(() => {
      getFiles(filters, true).then(() => {});
    }, 300);

    // Cleanup: clear timeout if keyword changes or component unmounts
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [filters.keyword, getFiles]);

  useEffect(() => {
    return () => {
      searchAbortRef.current?.abort();
    };
  }, []);

  /**
   * Re-open the dropdown after loading, if search keyword and 'keep open' ref are set.
   * Small delay (50ms) to avoid fight with Ant Design popup close timing.
   */
  useEffect(() => {
    if (!isLoading && filters.keyword.trim() && shouldKeepOpenRef.current) {
      const timeoutId = setTimeout(() => {
        if (filters.keyword.trim() && shouldKeepOpenRef.current) {
          setIsOpen(true);
        }
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, filters.keyword]);

  return (
    <>
      <Select
        key={selectKey}
        aria-label={t('a11y.globalSearch')}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        // Custom suffix: Exact match toggle button and loading spinner
        suffixIcon={
          <div className="relative flex h-5 w-7 items-center justify-center">
            <Spin size="small" spinning={isLoading}>
              <Tooltip
                title={
                  filters.exactMatch ? t('common.filterExactMatch') : t('common.filterWholeWord')
                }
              >
                <Button
                  type="text"
                  size="small"
                  aria-label={
                    filters.exactMatch ? t('a11y.searchWholeWord') : t('a11y.searchExactMatch')
                  }
                  disabled={isLoading}
                  onClick={handleExactMatchChange}
                >
                  {filters.exactMatch ? (
                    <Icon name="WholeWordIcon" className="h-5 w-5" />
                  ) : (
                    <Icon name="SquareAsteriskIcon" className="h-5 w-5" />
                  )}
                </Button>
              </Tooltip>
            </Spin>
            {/* Code for displaying loading overlays or Cmd+K hints can be added here if needed */}
          </div>
        }
        ref={selectRef}
        className={className ?? 'lg:min-w-[500px] [&_.ant-select-selector]:rounded-full!'}
        showSearch={{
          onSearch: handleSearch,
          filterOption: false,
        }}
        onInputKeyDown={(e) => {
          // Allow typing space in search: rc-select calls preventDefault() on Space
          // for ARIA/scroll, which blocks the character. Stop propagation so the
          // event never reaches the container and the input can receive the space.
          if (e.key === ' ') {
            e.stopPropagation();
          }
        }}
        labelInValue
        value={selectedFileUid ? { value: selectedFileUid, label: '' } : undefined}
        open={isOpen}
        onOpenChange={(open) => {
          // Prevent closing if loading, allow otherwise
          if (!open && isLoading) {
            // Schedule re-open if still loading, prevents accidental closure
            requestAnimationFrame(() => {
              if (isLoading) {
                setIsOpen(true);
              }
            });
            return;
          }

          if (!open) {
            shouldKeepOpenRef.current = false;
            // Reset filter and results when dropdown closes
            setFilters((prevFilters) => ({
              ...prevFilters,
              keyword: '',
              offset: 0,
            }));
            setFiles([]);
            setSelectedFileUid(null);
            setFilesTotal(0);
          }

          setIsOpen(open);
        }}
        loading={isLoading}
        prefix={<Icon name="SearchIcon" className="text-gray-dark h-4 w-4" />}
        placeholder={t('common.globalSearchPlaceholder')}
        notFoundContent={
          // Show appropriate message based on search/loading state
          isLoading
            ? t('common.loading')
            : filters.keyword.trim() && files.length === 0
              ? t('common.noFilesFound')
              : null
        }
        onPopupScroll={(e) => {
          // Infinite scroll: load more when scrolled to bottom
          const target = e.target as HTMLDivElement;
          if (target.scrollTop + target.clientHeight === target.scrollHeight) {
            handleLoadMore();
          }
        }}
        onSelect={(_value, option) => {
          // When a file is selected, navigate to its location
          // Use onSelect instead of onChange to intercept before Select updates its internal state
          if (option && 'data' in option) {
            shouldKeepOpenRef.current = false;
            // Navigate to the file location first
            redirectFileToLocation({ file: option.data as File });
            // Reset everything immediately
            setSelectedFileUid(null);
            setFilters((prevFilters) => ({
              ...prevFilters,
              keyword: '',
              offset: 0,
            }));
            setFiles([]);
            setFilesTotal(0);
            setIsOpen(false);
            // Force Select to re-render by changing the key, which resets its internal state
            // This prevents the file name from appearing as placeholder
            setSelectKey((prev) => prev + 1);
            onAfterSelect?.();
          }
        }}
        onChange={() => {
          // Prevent onChange from setting the value - we handle everything in onSelect
          // This ensures the Select doesn't display the selected file name
        }}
        options={files.map((file) => ({
          value: file.id,
          label: file.fileName,
          data: file,
        }))}
        optionRender={(option) => (
          // Option rendering: show file icon, name, tags, and file meta info
          <div className="flex w-full flex-row items-center justify-between">
            <div className="relative flex min-w-0 flex-1 flex-row items-center gap-2">
              {/* File icon (with shortcut/folder coloring) */}
              <FileIcon
                //fileId={option.data.data.id}
                isFolder={option.data.data.isFolder}
                fileExtension={removeShortcutExtension(option.data.data.fileExtension)}
                secretPreset={option.data.data.secretPreset}
                isShortcut={option.data.data.isShortcut}
                style={{
                  color: getFolderHex(option.data.data),
                }}
                className="h-5 w-5"
              />
              {/* Favorite/star indicator */}
              {option.data.data.isFavourite && (
                <Icon className="absolute -top-1 left-3 w-3" name="StarShadowIcon" />
              )}

              <div className="flex min-w-0 flex-1 flex-col">
                {/* File name and tags */}
                <div className="flex flex-row items-center gap-1">
                  {/* File name (with title tooltip) */}
                  <span
                    title={removeShortcutExtension(getFileTitle(option.data.data))}
                    className="text-blue-dark truncate overflow-hidden text-sm whitespace-nowrap"
                  >
                    {removeShortcutExtension(getFileTitle(option.data.data))}
                  </span>
                  {option.data.data.isDeleted && (
                    <Icon name="TrashIcon" className="text-red-dark h-3 w-3" />
                  )}
                  <div className="flex items-center gap-2">
                    {/* Render the first visible tag, if present */}
                    {option.data.data.tags?.map((optionTag, index) => {
                      if (index < 1) {
                        const tag = tags.find((t) => t.id === optionTag.id);
                        return (
                          tag?.fileVisible && (
                            <Tag
                              key={optionTag.id}
                              className="m-0 h-5 text-xs leading-3"
                              style={getTagPresentationStyle(tag?.color, { darkMode: isDarkMode })}
                            >
                              <div className="flex flex-row items-center justify-center text-xs lg:justify-between">
                                <div />
                                {/* Tag title with truncation/tooltip if needed */}
                                <span className="max-w-28">
                                  <TruncatedTooltip>{tag?.title}</TruncatedTooltip>
                                </span>
                                <div className="flex flex-row items-center gap-1">
                                  {tag?.isUsedForFileVisibility ? (
                                    <Tooltip title={t('common.tagIsUsedForFileVisibility')}>
                                      <div>
                                        <Icon
                                          name="Share2Icon"
                                          className="text-gray-dark h-3 w-3"
                                        />
                                      </div>
                                    </Tooltip>
                                  ) : (
                                    <div className="w-1" />
                                  )}
                                  {/* Public tag indicator icon */}
                                  {tag.publicTag ? (
                                    <Icon name="BuildingIcon" className="text-gray-dark h-3 w-3" />
                                  ) : (
                                    <div className="w-1" />
                                  )}
                                </div>
                              </div>
                            </Tag>
                          )
                        );
                      }
                    })}
                    {/* If there are multiple tags, offer a popover for extras */}
                    {option.data.data.tags?.length > 1 && (
                      <Popover
                        zIndex={9999}
                        content={
                          <div className="flex w-full flex-col gap-2">
                            {option.data.data.tags
                              ?.slice(1, option.data.data.tags.length)
                              .map((optionTag) => {
                                const tag = tags.find((t) => t.id === optionTag.id);
                                return (
                                  tag?.fileVisible && (
                                    <Tag
                                      key={tag.id}
                                      style={getTagPresentationStyle(tag.color, {
                                        darkMode: isDarkMode,
                                      })}
                                    >
                                      <div className="text-xxs flex w-32 flex-row items-center justify-center lg:justify-between">
                                        <div />
                                        <span className="max-w-28">
                                          <TruncatedTooltip>{tag.title}</TruncatedTooltip>
                                        </span>
                                        {/* Public tag icon for tags in popover */}
                                        {tag.publicTag ? (
                                          <Icon
                                            name="BuildingIcon"
                                            className="text-gray-dark h-4 w-4"
                                          />
                                        ) : (
                                          <div className="w-1" />
                                        )}
                                      </div>
                                    </Tag>
                                  )
                                );
                              })}
                          </div>
                        }
                        trigger="hover"
                      >
                        <Button size="small" shape="circle" className="h-4.5! w-4.5! min-w-0! p-0">
                          <span className="text-xxs">+{option.data.data.tags?.length - 1}</span>
                        </Button>
                      </Popover>
                    )}
                  </div>
                </div>

                {/* File meta info: last modified and sharedBy */}
                <div className="flex flex-row items-center gap-2">
                  <span className="text-gray-dark text-xs">
                    {t('common.lastModified')}
                    {': '}
                    {formatTimestamp(dateFromTicks(option.data.data.lastModifiedAt)) || '-'}
                  </span>
                  {/* Show sharedBy info if not "Me" */}
                  {option.data.data.sharedBy !== 'Me' && (
                    <span className="border-gray-medium text-gray-dark max-w-[80%] truncate border-l pl-2 text-xs">
                      <TruncatedTooltip>
                        {t('common.sharedBy')} {option.data.data.sharedBy}
                      </TruncatedTooltip>
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* File location shortcut button (not available for folders) */}
            {!option.data.data.isFolder && (
              <Tooltip placement="right" title={t('common.openFileLocation')}>
                <Button
                  type="text"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Open file location but do not open the viewer
                    redirectFileToLocation({ file: option.data.data, avoidOpenViewer: true });
                    onAfterSelect?.();
                  }}
                >
                  <Icon name="ExternalLinkIcon" className="text-gray-dark h-4 w-4" />
                </Button>
              </Tooltip>
            )}
          </div>
        )}
      />
    </>
  );
}
